import React from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { ChakraProvider, ColorModeScript } from '@chakra-ui/react';
import { HashRouter } from 'react-router-dom';
import App from './App';
import { theme } from './utils/helpers';
import { AuthService } from './services/authService';
import config from './config/config';

class ApplicationBootstrap {
    constructor() {
        this.config = config;
        this.msalInstance = null;
        this.root = null;
        this.isProduction = process.env.NODE_ENV === 'production';
    }

    // Load and validate configuration
    async loadConfiguration() {
        try {
            this.validateConfiguration();
            window.ADI_CONFIG = this.config;
            return this.config;
        } catch (error) {
            console.error('Configuration validation failed:', error);
            this.createFallbackConfiguration();
            return this.config;
        }
    }

    // Validate required configuration fields
    validateConfiguration() {
        if (!this.config) {
            throw new Error('Configuration is null or undefined');
        }

        if (!this.config.msal?.auth?.clientId) {
            throw new Error('MSAL client ID is required in configuration');
        }

        // Apply defaults for missing optional fields
        this.config.msal.auth.authority = this.config.msal.auth.authority || 'https://login.microsoftonline.com/common';
        this.config.msal.auth.redirectUri = this.config.msal.auth.redirectUri || window.location.origin;
        this.config.msal.cache = this.config.msal.cache || {};
        this.config.msal.cache.cacheLocation = this.config.msal.cache.cacheLocation || 'sessionStorage';
        this.config.msal.cache.storeAuthStateInCookie = this.config.msal.cache.storeAuthStateInCookie || false;

        this.config.api = this.config.api || {};
        this.config.api.baseUrl = this.config.api.baseUrl || 'http://localhost:8080';
        this.config.api.timeout = this.config.api.timeout || 30000;
        this.config.api.retryAttempts = this.config.api.retryAttempts || 3;

        this.config.app = this.config.app || {};
        this.config.app.name = this.config.app.name || 'Email Dashboard';
        this.config.app.version = this.config.app.version || '1.0.0';
        this.config.app.environment = this.config.app.environment || process.env.NODE_ENV || 'production';
        this.config.app.buildTime = this.config.app.buildTime || new Date().toISOString();
    }

    // Create fallback configuration
    createFallbackConfiguration() {
        this.config = {
            api: { 
                baseUrl: 'http://localhost:8080',
                timeout: 30000,
                retryAttempts: 3
            },
            msal: {
                auth: {
                    clientId: process.env.REACT_APP_MSAL_CLIENT_ID || 'development-client-id',
                    authority: process.env.REACT_APP_MSAL_AUTHORITY || 'https://login.microsoftonline.com/common',
                    redirectUri: window.location.origin,
                    navigateToLoginRequestUrl: false
                },
                cache: {
                    cacheLocation: 'sessionStorage',
                    storeAuthStateInCookie: false
                },
                scopes: ['User.Read', 'openid', 'profile', 'email']
            },
            app: {
                name: 'Email Dashboard',
                version: '1.0.0',
                environment: process.env.NODE_ENV || 'development',
                buildTime: new Date().toISOString(),
                features: {
                    autoRefresh: {
                        dashboard: 15000,
                        heartbeat: 15000,
                        reportResults: 30000,
                        notifications: 30000
                    },
                    timeZone: {
                        default: 'local',
                        utcOffset: '+05:30',
                        allowSwitch: true
                    },
                    notifications: {
                        autoHideDuration: 5000,
                        showDesktopNotifications: false
                    }
                }
            }
        };
        
        window.ADI_CONFIG = this.config;
    }

    // Initialize MSAL authentication instance
    async initializeMSAL() {
        try {
            const msalConfig = {
                auth: {
                    clientId: this.config.msal.auth.clientId,
                    authority: this.config.msal.auth.authority,
                    redirectUri: this.config.msal.auth.redirectUri,
                    navigateToLoginRequestUrl: false,
                    postLogoutRedirectUri: window.location.origin
                },
                cache: {
                    cacheLocation: this.config.msal.cache.cacheLocation,
                    storeAuthStateInCookie: this.config.msal.cache.storeAuthStateInCookie
                },
                system: {
                    loggerOptions: {
                        loggerCallback: (level, message, containsPii) => {
                            if (containsPii) return;
                            
                            const timestamp = new Date().toISOString();
                            const logMessage = `[MSAL] [${timestamp}] ${message}`;
                            
                            switch (level) {
                                case 'Error':
                                    console.error(logMessage);
                                    break;
                                case 'Warning':
                                    console.warn(logMessage);
                                    break;
                                default:
                                    // if (!this.isProduction) {
                                    //     console.log(logMessage);
                                    // }
                            }
                        },
                        piiLoggingEnabled: false,
                        logLevel: this.isProduction ? 'Error' : 'Info'
                    },
                    allowNativeBroker: false,
                    windowHashTimeout: 60000,
                    iframeHashTimeout: 6000,
                    loadFrameTimeout: 0
                },
                telemetry: {
                    application: {
                        appName: this.config.app.name,
                        appVersion: this.config.app.version
                    }
                }
            };

            this.msalInstance = new PublicClientApplication(msalConfig);
            await this.msalInstance.initialize();
            return this.msalInstance;
            
        } catch (error) {
            console.error('MSAL initialization failed:', {
                error: error.message,
                stack: error.stack,
                config: {
                    clientId: this.config.msal.auth.clientId.substring(0, 8) + '...',
                    authority: this.config.msal.auth.authority,
                    cacheLocation: this.config.msal.cache.cacheLocation
                }
            });
            throw new Error(`Authentication system initialization failed: ${error.message}`);
        }
    }

    // Initialize AuthService with MSAL instance
    async initializeAuthService() {
        try {
            if (!this.msalInstance) {
                throw new Error('MSAL instance is required for AuthService initialization');
            }

            AuthService.initializeMsal(this.msalInstance);
            
        } catch (error) {
            console.error('AuthService initialization failed:', {
                error: error.message,
                stack: error.stack,
                msalInstance: !!this.msalInstance
            });
            throw new Error(`AuthService initialization failed: ${error.message}`);
        }
    }

    // Render React application with all providers
    renderApplication() {
        try {
            const root = ReactDOM.createRoot(document.getElementById('root'));
            
            const loadingElement = document.getElementById('app-loading');
            if (loadingElement) {
                loadingElement.style.display = 'none';
            }
            
            document.body.classList.add('app-loaded');
            document.body.classList.add(`env-${this.config.app.environment}`);

            root.render(
                //Vishnu modified
                // <React.StrictMode>
                //     <ColorModeScript initialColorMode={theme.config.initialColorMode} />
                //     <ChakraProvider theme={theme}>
                //         <MsalProvider instance={this.msalInstance}>
                //             <BrowserRouter
                //                 future={{
                //                     v7_startTransition: true,
                //                     v7_relativeSplatPath: true
                //                 }}
                //                 basename={this.config.app.subDirectory}
                //             >
                //                 <App />
                //             </BrowserRouter>
                //         </MsalProvider>
                //     </ChakraProvider>
                // </React.StrictMode>

                <React.StrictMode>
                    <ColorModeScript initialColorMode={theme.config.initialColorMode} />
                    <ChakraProvider theme={theme}>
                        <MsalProvider instance={this.msalInstance}>
                            <HashRouter
                                future={{
                                    v7_startTransition: true,
                                    v7_relativeSplatPath: true
                                }}
                            >
                                <App />
                            </HashRouter>
                        </MsalProvider>
                    </ChakraProvider>
                </React.StrictMode>
            );

            this.root = root;
            
        } catch (error) {
            console.error('Application rendering failed:', error);
            this.renderErrorState(error);
        }
    }

    // Main application bootstrap method
    async bootstrap() {
        const startTime = performance.now();
        
        try {
            await this.loadConfiguration();
            await this.initializeMSAL();
            await this.initializeAuthService();
            this.renderApplication();
            
        } catch (error) {
            console.error('Application bootstrap failed:', {
                error: error.message,
                stack: error.stack,
                timestamp: new Date().toISOString(),
                bootstrapTime: Math.round(performance.now() - startTime)
            });
            
            this.renderErrorState(error);
        }
    }

    // Render error state if bootstrap fails
    renderErrorState(error) {
        try {
            const root = ReactDOM.createRoot(document.getElementById('root'));
            
            const loadingElement = document.getElementById('app-loading');
            if (loadingElement) {
                loadingElement.style.display = 'none';
            }
            
            let errorTitle = 'Application Startup Failed';
            let errorMessage = 'The Email Dashboard could not be initialized. Please try refreshing the page.';
            let technicalDetails = error.message;
            
            if (error.message?.includes('Configuration')) {
                errorTitle = 'Configuration Error';
                errorMessage = 'There was an issue loading the application configuration. Please check your network connection and try again.';
            } else if (error.message?.includes('Authentication')) {
                errorTitle = 'Authentication Setup Error';
                errorMessage = 'The authentication system could not be initialized. Please contact your system administrator.';
            } else if (error.message?.includes('Network')) {
                errorTitle = 'Network Error';
                errorMessage = 'Unable to connect to required services. Please check your internet connection.';
            }
            
            root.render(
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: '#f4f7fe',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                }}>
                    <div style={{
                        maxWidth: '600px',
                        backgroundColor: 'white',
                        padding: '40px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        border: '1px solid #e2e8f0'
                    }}>
                        <h1 style={{ 
                            color: '#e53e3e', 
                            marginBottom: '16px',
                            fontSize: '24px',
                            fontWeight: '600'
                        }}>
                            {errorTitle}
                        </h1>
                        
                        <p style={{ 
                            color: '#4a5568', 
                            marginBottom: '24px', 
                            fontSize: '16px',
                            lineHeight: '1.5'
                        }}>
                            {errorMessage}
                        </p>
                        
                        {!this.isProduction && (
                            <div style={{
                                backgroundColor: '#f7fafc',
                                padding: '16px',
                                borderRadius: '8px',
                                marginBottom: '24px',
                                border: '1px solid #e2e8f0'
                            }}>
                                <p style={{ 
                                    color: '#718096', 
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    marginBottom: '8px'
                                }}>
                                    Technical Details:
                                </p>
                                <p style={{ 
                                    color: '#718096', 
                                    fontSize: '12px',
                                    fontFamily: 'monospace',
                                    wordBreak: 'break-word'
                                }}>
                                    {technicalDetails}
                                </p>
                            </div>
                        )}
                        
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <button
                                onClick={() => window.location.reload()}
                                style={{
                                    backgroundColor: '#4318ff',
                                    color: 'white',
                                    border: 'none',
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#3311cc'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#4318ff'}
                            >
                                Reload Application
                            </button>
                            
                            <button
                                onClick={() => {
                                    localStorage.clear();
                                    sessionStorage.clear();
                                    window.location.reload();
                                }}
                                style={{
                                    backgroundColor: 'transparent',
                                    color: '#4318ff',
                                    border: '2px solid #4318ff',
                                    padding: '10px 22px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    transition: 'all 0.2s'
                                }}
                                onMouseOver={(e) => {
                                    e.target.style.backgroundColor = '#4318ff';
                                    e.target.style.color = 'white';
                                }}
                                onMouseOut={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.color = '#4318ff';
                                }}
                            >
                                Clear Cache & Reload
                            </button>
                        </div>
                        
                        <p style={{
                            color: '#a0aec0',
                            fontSize: '12px',
                            marginTop: '24px'
                        }}>
                            If the problem persists, please contact your system administrator.
                        </p>
                    </div>
                </div>
            );
        } catch (renderError) {
            console.error('Failed to render error state:', renderError);
            
            document.body.innerHTML = `
                <div style="
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    height: 100vh;
                    background-color: #f4f7fe;
                    font-family: Arial, sans-serif;
                    text-align: center;
                    padding: 20px;
                ">
                    <div>
                        <h1 style="color: #e53e3e;">Critical Application Error</h1>
                        <p style="color: #4a5568;">Please refresh the page or contact support.</p>
                        <button onclick="window.location.reload()" style="
                            background-color: #4318ff;
                            color: white;
                            border: none;
                            padding: 12px 24px;
                            border-radius: 8px;
                            cursor: pointer;
                            font-size: 16px;
                            margin-top: 16px;
                        ">Reload</button>
                    </div>
                </div>
            `;
        }
    }
}

// Global error handlers
window.addEventListener('error', (event) => {
    console.error('Global error caught:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
    });
});

window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', {
        reason: event.reason,
        promise: event.promise
    });
});

// Initialize application
const bootstrap = new ApplicationBootstrap();
bootstrap.bootstrap().catch(error => {
    console.error('Critical application bootstrap error:', error);
    
    setTimeout(() => {
        const root = document.getElementById('root');
        if (root && !root.hasChildNodes()) {
            root.innerHTML = `
                <div style="
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    height: 100vh;
                    background-color: #f4f7fe;
                    font-family: Arial, sans-serif;
                    text-align: center;
                ">
                    <div>
                        <h1 style="color: #e53e3e;">Application Failed to Start</h1>
                        <p>Please refresh the page</p>
                        <button onclick="window.location.reload()" style="
                            background-color: #4318ff;
                            color: white;
                            border: none;
                            padding: 12px 24px;
                            border-radius: 8px;
                            cursor: pointer;
                        ">Reload</button>
                    </div>
                </div>
            `;
        }
    }, 1000);
});